/**
 * Trovva — Agent Filter Combobox
 *
 * Reusable fuzzy-search + dropdown for filtering by agent.
 * Used in Tasks tab, Calendar tab, and anywhere broker needs agent scoping.
 *
 * Props:
 *   agents       — [{ id, name, email?, first_name?, last_name?, full_name? }]
 *   value        — 'mine' | 'all' | agent UUID
 *   onChange      — (value) => void
 *   mineLabel    — label for self option (default: "My Tasks")
 *   allLabel     — label for all option (default: "All Agents")
 *   dataTour     — optional data-tour attribute
 */
import { useState, useEffect, useRef } from 'react';
import { User, ChevronDown, Search, X } from 'lucide-react';
import { theme } from '../../lib/theme';

export default function AgentFilterCombo({
  agents = [],
  value = 'mine',
  onChange,
  mineLabel = 'My Tasks',
  allLabel = 'All Agents',
  dataTour,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getAgentName = (a) => {
    if (a.full_name) return a.full_name;
    if (a.name) return a.name;
    const parts = [a.first_name, a.last_name].filter(Boolean);
    if (parts.length) return parts.join(' ');
    return a.email?.split('@')[0] || 'Agent';
  };

  const displayLabel = value === 'mine' ? mineLabel
    : value === 'all' ? allLabel
    : (() => {
        const a = agents.find(a => a.id === value);
        return a ? getAgentName(a) : 'Agent';
      })();

  const filtered = agents.filter(a => {
    if (!query) return true;
    const q = query.toLowerCase();
    const name = getAgentName(a).toLowerCase();
    const email = (a.email || '').toLowerCase();
    return name.includes(q) || email.includes(q);
  });

  const initials = (a) => {
    const name = getAgentName(a);
    return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  };

  const select = (val) => {
    onChange(val);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={ref} data-tour={dataTour} style={{ position: 'relative' }}>
      {/* Trigger button */}
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 10px', borderRadius: 8,
          border: `1px solid ${theme.colors.border}`,
          background: theme.colors.card, cursor: 'pointer',
          fontSize: 12, fontWeight: value !== 'mine' ? 600 : 400,
          color: theme.colors.text, minWidth: 130,
        }}
      >
        <User size={13} style={{ color: theme.colors.textLight, flexShrink: 0 }} />
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayLabel}
        </span>
        <ChevronDown size={12} style={{ color: theme.colors.textLight, flexShrink: 0 }} />
      </button>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 50,
          marginTop: 4, width: 240,
          background: theme.colors.card, border: `1px solid ${theme.colors.border}`,
          borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          overflow: 'hidden',
        }}>
          {/* Search input */}
          <div style={{ position: 'relative', padding: '8px 8px 4px' }}>
            <Search size={12} style={{ position: 'absolute', left: 16, top: 17, color: theme.colors.textLight }} />
            <input
              value={query} onChange={e => setQuery(e.target.value)}
              placeholder="Search agent…" autoFocus
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '7px 8px 7px 28px', borderRadius: 6,
                border: `1px solid ${theme.colors.border}`,
                fontSize: 12, outline: 'none', background: theme.colors.bg,
                color: theme.colors.text,
              }}
            />
            {query && (
              <button onClick={() => setQuery('')} style={{
                position: 'absolute', right: 14, top: 16,
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                color: theme.colors.textLight,
              }}>
                <X size={12} />
              </button>
            )}
          </div>

          {/* Fixed options: Mine + All */}
          <div style={{ borderBottom: `1px solid ${theme.colors.border}`, padding: '4px 0' }}>
            <OptionRow
              active={value === 'mine'}
              onClick={() => select('mine')}
              label={mineLabel}
              icon={<User size={12} />}
            />
            <OptionRow
              active={value === 'all'}
              onClick={() => select('all')}
              label={allLabel}
              icon={<span style={{ fontSize: 11 }}>👥</span>}
            />
          </div>

          {/* Agent list */}
          <div style={{ maxHeight: 200, overflowY: 'auto', padding: '4px 0' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '10px 12px', fontSize: 11, color: theme.colors.textLight, textAlign: 'center' }}>
                No agents match "{query}"
              </div>
            ) : (
              filtered.map(a => {
                const name = getAgentName(a);
                return (
                  <button key={a.id} onClick={() => select(a.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                    padding: '7px 12px', border: 'none', cursor: 'pointer',
                    background: value === a.id ? `${theme.colors.primary}10` : 'transparent',
                    textAlign: 'left', fontSize: 12,
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => { if (value !== a.id) e.currentTarget.style.background = theme.colors.bg; }}
                  onMouseLeave={e => { e.currentTarget.style.background = value === a.id ? `${theme.colors.primary}10` : 'transparent'; }}
                  >
                    <div style={{
                      width: 24, height: 24, borderRadius: 12, flexShrink: 0,
                      background: theme.colors.primary, color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 9, fontWeight: 700,
                    }}>
                      {initials(a)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, color: theme.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {name}
                      </div>
                      {a.email && (
                        <div style={{ fontSize: 10, color: theme.colors.textLight, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.email}
                        </div>
                      )}
                    </div>
                    {value === a.id && (
                      <div style={{ color: theme.colors.primary, fontSize: 13, fontWeight: 700 }}>✓</div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function OptionRow({ active, onClick, label, icon }) {
  return (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 8, width: '100%',
      padding: '7px 12px', border: 'none', cursor: 'pointer',
      background: active ? `${theme.colors.primary}10` : 'transparent',
      textAlign: 'left', fontSize: 12, fontWeight: active ? 600 : 400,
      color: theme.colors.text, transition: 'background 0.1s',
    }}
    onMouseEnter={e => { if (!active) e.currentTarget.style.background = theme.colors.bg; }}
    onMouseLeave={e => { e.currentTarget.style.background = active ? `${theme.colors.primary}10` : 'transparent'; }}
    >
      <div style={{ width: 24, display: 'flex', justifyContent: 'center' }}>{icon}</div>
      <span>{label}</span>
      {active && <span style={{ marginLeft: 'auto', color: theme.colors.primary, fontWeight: 700 }}>✓</span>}
    </button>
  );
}
